import { useState, useEffect } from 'react';
import { X, Package, Truck, Calendar, FileText, AlertTriangle, Hash, AlertCircle, Check, CheckCircle, RefreshCw } from 'lucide-react';
import API_URL from '../config/api';

const ShipmentForm = ({ onClose, onSuccess, laboratories, initialStudyId = null }) => {
    const [formData, setFormData] = useState({
        patient_study_ids: initialStudyId ? [initialStudyId] : [],
        laboratory_id: '',
        courier_name: '',
        tracking_number: '',
        package_description: '',
        notes: '',
        status: 'preparando'
    });
    // Add state to store the "raw" lab name from catalog when no ID is matched
    const [catalogLabName, setCatalogLabName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pendingStudies, setPendingStudies] = useState([]);
    const [loadingStudies, setLoadingStudies] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!initialStudyId) {
            fetchPendingStudies();
        } else {
            setLoadingStudies(false);
        }
    }, [initialStudyId]);

    const fetchPendingStudies = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/lab/studies`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const ready = data.filter(s =>
                    (s.status === 'muestra_tomada' || s.status === 'solicitado') &&
                    Number(s.active_shipments) === 0
                );
                setPendingStudies(ready);
            }
        } catch (err) {
            console.error('Error fetching studies:', err);
        } finally {
            setLoadingStudies(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const findLabIdByName = (labName) => {
        if (!labName || !laboratories) return '';
        const lab = laboratories.find(l => 
            l.name.toLowerCase().trim() === labName.toLowerCase().trim()
        );
        return lab ? lab.id : '';
    };

    const toggleStudy = (studyId) => {
        setFormData(prev => {
            const isSelected = prev.patient_study_ids.includes(studyId);
            const newIds = isSelected
                ? prev.patient_study_ids.filter(id => id !== studyId)
                : [...prev.patient_study_ids, studyId];

            // Auto-select lab based on the FIRST selected study
            let newLabId = prev.laboratory_id;
            let currentCatalogName = '';
            
            if (newIds.length > 0) {
                const firstStudy = pendingStudies.find(s => s.id === newIds[0]);
                if (firstStudy) {
                    currentCatalogName = firstStudy.catalog_laboratory || '';
                    newLabId = findLabIdByName(currentCatalogName);
                }
            } else {
                newLabId = '';
            }

            setCatalogLabName(currentCatalogName);

            return {
                ...prev,
                patient_study_ids: newIds,
                laboratory_id: newLabId
            };
        });
    };

    const removeStudy = (studyId) => {
        setFormData(prev => {
            const newIds = prev.patient_study_ids.filter(id => id !== studyId);
            
            let newLabId = prev.laboratory_id;
            let currentCatalogName = '';
            
            if (newIds.length > 0) {
                const firstStudy = pendingStudies.find(s => s.id === newIds[0]);
                if (firstStudy) {
                    currentCatalogName = firstStudy.catalog_laboratory || '';
                    newLabId = findLabIdByName(currentCatalogName);
                }
            } else {
                newLabId = '';
            }

            setCatalogLabName(currentCatalogName);

            return {
                ...prev,
                patient_study_ids: newIds,
                laboratory_id: newLabId
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (formData.patient_study_ids.length === 0) {
            setError('Debe seleccionar al menos un estudio');
            setLoading(false);
            return;
        }

        if (!formData.courier_name || formData.courier_name === '') {
            setError('Debe seleccionar una paquetería');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const payload = {
                ...formData,
                laboratory_id: formData.laboratory_id || null
            };

            const response = await fetch(`${API_URL}/shipments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                onSuccess();
            } else {
                setError(data.message || 'Error al crear el envío');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const courierOptions = [
        'DHL',
        'FedEx',
        'Estafeta',
        'UPS',
        'Redpack',
        'Mensajero propio',
        'Otro'
    ];

    // Filter studies by search term
    const filteredStudies = pendingStudies.filter(study => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            (study.study_name || '').toLowerCase().includes(term) ||
            (study.patient_first_name || '').toLowerCase().includes(term) ||
            (study.patient_last_name || '').toLowerCase().includes(term)
        );
    });

    // Group studies by patient
    const studiesByPatient = filteredStudies.reduce((acc, study) => {
        const patientKey = `${study.patient_first_name} ${study.patient_last_name}`;
        if (!acc[patientKey]) acc[patientKey] = [];
        acc[patientKey].push(study);
        return acc;
    }, {});

    // Get selected studies info
    const selectedStudies = pendingStudies.filter(s => formData.patient_study_ids.includes(s.id));

    // Check if selected studies go to different laboratories
    const selectedLabs = [...new Set(selectedStudies.map(s => s.catalog_laboratory).filter(Boolean))];
    const hasMultipleLabs = selectedLabs.length > 1;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <style>{`
                .form-row-custom {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 20px;
                }
                .form-group-custom {
                    flex: 1;
                    min-width: 0;
                }
                @media (max-width: 600px) {
                    .form-row-custom {
                        flex-direction: column;
                        gap: 15px;
                    }
                }
                .form-group-custom label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    min-height: 24px;
                    margin-bottom: 0.5rem;
                }
                .shipment-form input, 
                .shipment-form select {
                    height: 42px;
                }
                .study-selector {
                    border: 1px solid rgba(0, 187, 191, 0.15);
                    border-radius: 12px;
                    max-height: 260px;
                    overflow-y: auto;
                    background: linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                }
                .study-selector::-webkit-scrollbar {
                    width: 5px;
                }
                .study-selector::-webkit-scrollbar-track {
                    background: transparent;
                }
                .study-selector::-webkit-scrollbar-thumb {
                    background: rgba(0, 187, 191, 0.25);
                    border-radius: 10px;
                }
                .study-search {
                    position: sticky;
                    top: 0;
                    padding: 10px;
                    background: rgba(248, 250, 252, 0.95);
                    backdrop-filter: blur(8px);
                    border-bottom: 1px solid rgba(0, 187, 191, 0.1);
                    z-index: 1;
                }
                .study-search input {
                    width: 100%;
                    padding: 8px 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 13px;
                    height: 38px !important;
                    background: white;
                    transition: all 0.2s ease;
                    outline: none;
                }
                .study-search input:focus {
                    border-color: #00BBBF;
                    box-shadow: 0 0 0 3px rgba(0, 187, 191, 0.1);
                }
                .study-search input::placeholder {
                    color: #a0aec0;
                }
                .patient-group {
                    padding: 0;
                }
                .patient-group-header {
                    font-size: 10px;
                    font-weight: 700;
                    color: #00959A;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    padding: 10px 14px 6px;
                    background: rgba(0, 187, 191, 0.04);
                    border-bottom: 1px solid rgba(0, 187, 191, 0.08);
                }
                .study-checkbox-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px 14px;
                    cursor: pointer;
                    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
                    transition: all 0.2s ease;
                }
                .study-checkbox-item:hover {
                    background: rgba(0, 187, 191, 0.05);
                }
                .study-checkbox-item:last-child {
                    border-bottom: none;
                }
                .study-checkbox-item.selected {
                    background: rgba(0, 187, 191, 0.08);
                }
                .study-checkbox {
                    width: 20px;
                    height: 20px;
                    border-radius: 6px;
                    border: 2px solid #cbd5e1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.2s ease;
                }
                .study-checkbox-item:hover .study-checkbox {
                    border-color: #00BBBF;
                }
                .study-checkbox.checked {
                    background: linear-gradient(135deg, #00BBBF 0%, #00959A 100%);
                    border-color: transparent;
                    box-shadow: 0 2px 6px rgba(0, 187, 191, 0.3);
                }
                .study-checkbox-label {
                    font-size: 13px;
                    color: #1e293b;
                    flex: 1;
                    font-weight: 500;
                }
                .study-checkbox-label .study-lab {
                    font-size: 11px;
                    color: #94a3b8;
                    display: block;
                    font-weight: 400;
                    margin-top: 1px;
                }
                .selected-studies-summary {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-top: 10px;
                }
                .selected-study-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: linear-gradient(135deg, rgba(0, 187, 191, 0.1) 0%, rgba(0, 149, 154, 0.08) 100%);
                    color: #00797C;
                    font-size: 12px;
                    font-weight: 500;
                    padding: 4px 10px;
                    border-radius: 16px;
                    border: 1px solid rgba(0, 187, 191, 0.2);
                    transition: all 0.15s ease;
                }
                .selected-study-tag:hover {
                    background: rgba(0, 187, 191, 0.15);
                }
                .selected-study-tag button {
                    background: none;
                    border: none;
                    color: #00797C;
                    cursor: pointer;
                    padding: 0;
                    display: flex;
                    font-size: 15px;
                    line-height: 1;
                    transition: color 0.15s;
                }
                .selected-study-tag button:hover {
                    color: #ef4444;
                }
                .lab-warning {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: #fffbeb;
                    color: #92400e;
                    font-size: 13px;
                    padding: 12px 16px;
                    border-radius: 10px;
                    margin-top: 15px;
                    border: 1px solid #fef3c7;
                    font-weight: 500;
                }
                .lab-warning.error {
                    background: #fee2e2;
                    color: #991b1b;
                    border-color: #fecaca;
                    animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
                }
                @keyframes shake {
                    10%, 90% { transform: translate3d(-1px, 0, 0); }
                    20%, 80% { transform: translate3d(2px, 0, 0); }
                    30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
                    40%, 60% { transform: translate3d(4px, 0, 0); }
                }
                .lab-display-box {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    padding: 10px 14px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-height: 42px;
                }
                .lab-name-tag {
                    color: #0369a1;
                    font-weight: 600;
                }
                .lab-id-hint {
                    color: #94a3b8;
                    font-size: 11px;
                    font-family: monospace;
                }
            `}</style>
            <div className="modal-content shipment-form-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><Package size={24} /> Crear Nuevo Envío</h2>
                    <button onClick={onClose} className="close-btn">
                        <X size={24} />
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="shipment-form">
                    {/* Study Selection - Multi-select with checkboxes */}
                    <div className="form-group">
                        <label>Muestras a enviar * ({formData.patient_study_ids.length} seleccionadas)</label>
                        {loadingStudies ? (
                            <p>Cargando estudios...</p>
                        ) : initialStudyId ? (
                            <input type="text" value={`Estudio #${initialStudyId}`} disabled />
                        ) : (
                            <>
                                <div className="study-selector">
                                    <div className="study-search">
                                        <input
                                            type="text"
                                            placeholder="Filtrar por estudio o paciente..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    {Object.keys(studiesByPatient).length === 0 ? (
                                        <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                                            <AlertTriangle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                            No hay muestras listas para enviar
                                        </div>
                                    ) : (
                                        Object.entries(studiesByPatient).map(([patient, studies]) => (
                                            <div key={patient} className="patient-group">
                                                <div className="patient-group-header">{patient}</div>
                                                {studies.map(study => {
                                                    const isSelected = formData.patient_study_ids.includes(study.id);
                                                    return (
                                                        <div
                                                            key={study.id}
                                                            className={`study-checkbox-item ${isSelected ? 'selected' : ''}`}
                                                            onClick={() => toggleStudy(study.id)}
                                                        >
                                                            <div className={`study-checkbox ${isSelected ? 'checked' : ''}`}>
                                                                {isSelected && <Check size={14} color="white" />}
                                                            </div>
                                                            <div className="study-checkbox-label">
                                                                {study.study_name}
                                                                {study.catalog_laboratory && (
                                                                    <span className="study-lab">Lab: {study.catalog_laboratory}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* Selected studies tags */}
                                {selectedStudies.length > 0 && (
                                    <div className="selected-studies-summary">
                                        {selectedStudies.map(study => (
                                            <span key={study.id} className="selected-study-tag">
                                                {study.study_name}
                                                <button type="button" onClick={() => removeStudy(study.id)}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Warning if multi-lab */}
                                {hasMultipleLabs && (
                                    <div className="lab-warning error">
                                        <AlertCircle size={18} />
                                        <div>
                                            <strong>¡Atención!</strong> Estudios para diferentes laboratorios: {selectedLabs.join(', ')}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Laboratory Display */}
                    <div className="form-group">
                        <label>Laboratorio Destino</label>
                        <div className="lab-display-box">
                            {catalogLabName || formData.laboratory_id ? (
                                <>
                                    <CheckCircle size={18} color="#00BBBF" />
                                    <span className="lab-name-tag">
                                        {catalogLabName || laboratories.find(l => l.id == formData.laboratory_id)?.name || 'Cargando...'}
                                    </span>
                                </>
                            ) : (
                                <span style={{ color: '#94a3b8' }}>
                                    Seleccione al menos una muestra para determinar el laboratorio
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Courier and Tracking */}
                    <div className="form-row-custom">
                        <div className="form-group-custom">
                            <label><Truck size={16} /> Paquetería</label>
                            <select
                                name="courier_name"
                                value={formData.courier_name}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione...</option>
                                {courierOptions.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group-custom">
                            <label><Hash size={16} /> Número de Guía</label>
                            <input
                                type="text"
                                name="tracking_number"
                                value={formData.tracking_number}
                                onChange={handleChange}
                                placeholder="Ej: 1234567890"
                            />
                        </div>
                    </div>



                    {/* Package Description */}
                    <div className="form-group">
                        <label><FileText size={16} /> Descripción del paquete</label>
                        <textarea
                            name="package_description"
                            value={formData.package_description}
                            onChange={handleChange}
                            placeholder="Ej: 2 tubos EDTA morado, 1 hisopo bucal..."
                            rows="2"
                        />
                    </div>

                    {/* Notes */}
                    <div className="form-group">
                        <label>Notas adicionales</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            placeholder="Instrucciones especiales, condiciones de envío..."
                            rows="2"
                        />
                    </div>

                    {/* Status */}
                    <div className="form-group">
                        <label>Estado inicial</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <option value="preparando">Preparando</option>
                            <option value="enviado">Ya enviado</option>
                        </select>
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={loading || formData.patient_study_ids.length === 0}
                            style={{ 
                                opacity: loading ? 0.7 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                position: 'relative'
                            }}
                        >
                            {loading ? (
                                <>
                                    <RefreshCw className="rotating" size={16} style={{ marginRight: '8px', display: 'inline-block' }} />
                                    Guardando...
                                </>
                            ) : `Crear Envío (${formData.patient_study_ids.length} muestras)`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ShipmentForm;
